import { useState, useRef, useCallback, useEffect } from "react";

import { getInitialChatWidth, saveChatWidth, constrainWidth } from "@/utils/panelWidth";

interface UsePersistentResizablePanelReturn {
  /** Current chat panel width as a percentage */
  chatWidth: number;
  /** Whether the user is currently resizing the panel */
  isResizing: boolean;
  /** Ref to attach to the container element */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Handler for starting the resize operation */
  handleMouseDown: (e: React.MouseEvent) => void;
}

/**
 * Hook for managing a resizable panel with localStorage persistence.
 * Handles mouse events for dragging, constrains width within bounds,
 * and saves the width to localStorage when resizing completes.
 */
export function usePersistentResizablePanel(): UsePersistentResizablePanelReturn {
  const [chatWidth, setChatWidth] = useState(getInitialChatWidth);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerRectRef = useRef<DOMRect | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (containerRef.current) {
      // Cache the container rect when starting to resize
      containerRectRef.current = containerRef.current.getBoundingClientRect();
    }
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRectRef.current) return;

      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Throttle using requestAnimationFrame
      animationFrameRef.current = requestAnimationFrame(() => {
        if (!containerRectRef.current) return;

        const newChatWidth =
          ((e.clientX - containerRectRef.current.left) / containerRectRef.current.width) * 100;

        // Constrain width between MIN_CHAT_WIDTH and MAX_CHAT_WIDTH
        const constrainedWidth = constrainWidth(newChatWidth);

        // Only update if the width has changed significantly (>0.1% to reduce unnecessary re-renders)
        setChatWidth((prevWidth) => {
          const diff = Math.abs(prevWidth - constrainedWidth);

          return diff > 0.1 ? constrainedWidth : prevWidth;
        });
      });
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    // Clean up animation frame and cached rect
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    containerRectRef.current = null;
    setIsResizing(false);

    // Save the current width to localStorage
    setChatWidth((currentWidth) => {
      saveChatWidth(currentWidth);

      return currentWidth;
    });
  }, []);

  // Add/remove global mouse event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return {
    chatWidth,
    isResizing,
    containerRef,
    handleMouseDown,
  };
}
